import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Tabs,
    Tab,
    Avatar,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    MenuItem,
    Alert,
    Snackbar,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Stack,
} from '@mui/material';
import {
    Edit as EditIcon,
    PhotoCamera as PhotoCameraIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Security as SecurityIcon,
    Settings as SettingsIcon,
    History as HistoryIcon,
    Person as PersonIcon,
    Api as ApiIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { profileService, UserProfile, UpdateProfileData, ChangePasswordData, UserSettings } from '../../services/profileService';
import PageLayout from '@/components/Layout/PageLayout';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`profile-tabpanel-${index}`}
            aria-labelledby={`profile-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const ProfilePage: React.FC = () => {
    const { user, updateProfile } = useAuth();
    const [currentTab, setCurrentTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState<UserProfile | null>(null);
    const [settings, setSettings] = useState<UserSettings | null>(null);
    const [activities, setActivities] = useState<any[]>([]);
    const [apiTokens, setApiTokens] = useState<any[]>([]);
    
    // Form states
    const [profileForm, setProfileForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
    });
    
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [tokenDialog, setTokenDialog] = useState(false);
    const [tokenName, setTokenName] = useState('');
    
    // UI states
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error' | 'info' | 'warning',
    });

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    // Load profile data on component mount
    useEffect(() => {
        loadProfileData();
        if (currentTab === 2) loadActivityLog();
        if (currentTab === 3) loadApiTokens();
    }, [currentTab]);

    // Initialize form with user data
    useEffect(() => {
        if (user) {
            setProfileForm({
                firstName: user.first_name || '',
                lastName: user.last_name || '',
                email: user.email || '',
                phoneNumber: user.phone_number || '',
            });
        }
    }, [user]);

    const loadProfileData = async () => {
        try {
            setLoading(true);
            const profile = await profileService.getProfile();
            console.log('Fetched profile:', profile);
            setProfileData(profile);
            
            // Try to load settings, but don't fail if endpoint doesn't exist
            // try {
            //     const userSettings = await profileService.getSettings();
            //     setSettings(userSettings);
            // } catch (settingsError: any) {
            //     // Settings endpoint not implemented yet, use defaults
            //     //console.log('Settings endpoint not available, using defaults');
            //     setSettings({
            //         emailNotifications: true,
            //         smsNotifications: false,
            //         theme: 'light',
            //         language: 'en',
            //         timezone: 'UTC'
            //     });
            // }
        } catch (error) {
            showSnackbar('Failed to load profile data', 'error');
            console.error('Profile load error:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadActivityLog = async () => {
        try {
            const response = await profileService.getActivityLog();
            setActivities(response.activities);
        } catch (error) {
            //console.log('Activity log endpoint not available');
            setActivities([]); // Set empty array instead of showing error
        }
    };

    const loadApiTokens = async () => {
        try {
            const tokens = await profileService.getApiTokens();
            setApiTokens(tokens);
        } catch (error) {
            //console.log('API tokens endpoint not available');
            setApiTokens([]); // Set empty array instead of showing error
        }
    };

    const handleProfileUpdate = async () => {
        try {
            setLoading(true);
            const updateData: UpdateProfileData = {
                firstName: profileForm.firstName,
                lastName: profileForm.lastName,
                email: profileForm.email,
                phoneNumber: profileForm.phoneNumber,
            };
            
            await profileService.updateProfile(updateData);
            await updateProfile(updateData as any);
            showSnackbar('Profile updated successfully');
            loadProfileData();
        } catch (error) {
            showSnackbar('Failed to update profile', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async () => {
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            showSnackbar('Passwords do not match', 'error');
            return;
        }
        
        try {
            setLoading(true);
            const passwordData: ChangePasswordData = {
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
                confirmPassword: passwordForm.confirmPassword,
            };
            
            await profileService.changePassword(passwordData);
            showSnackbar('Password changed successfully');
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            showSnackbar('Failed to change password', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsUpdate = async (newSettings: Partial<UserSettings>) => {
        try {
            const updatedSettings = await profileService.updateSettings(newSettings);
            setSettings(updatedSettings);
            showSnackbar('Settings updated successfully');
        } catch (error) {
            showSnackbar('Failed to update settings', 'error');
        }
    };

    const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setLoading(true);
            const profilePictureUrl = await profileService.uploadProfilePicture(file);
            
            // Refetch user profile to get the updated data
            const updatedProfile = await profileService.getProfile();
            
            // Update the local user state with the fresh data from server
            await updateProfile({ 
                profilePictureUrl: updatedProfile.profilePictureUrl,
                profile_picture: updatedProfile.profilePictureUrl 
            } as any);
            
            showSnackbar('Profile picture updated successfully');
            loadProfileData();
        } catch (error) {
            showSnackbar('Failed to upload profile picture', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateApiToken = async () => {
        if (!tokenName.trim()) {
            showSnackbar('Please enter a token name', 'error');
            return;
        }

        try {
            await profileService.generateApiToken(tokenName);
            showSnackbar('API token created successfully');
            setTokenDialog(false);
            setTokenName('');
            loadApiTokens();
        } catch (error) {
            showSnackbar('Failed to create API token', 'error');
        }
    };

    const handleRevokeApiToken = async (tokenId: number) => {
        try {
            await profileService.revokeApiToken(tokenId);
            showSnackbar('API token revoked successfully');
            loadApiTokens();
        } catch (error) {
            showSnackbar('Failed to revoke API token', 'error');
        }
    };

    if (loading && !profileData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <PageLayout>
            <Box
            component="main"
            sx={{
                flexGrow: 1,
                py: 3,
                width: { sm: `calc(100% - 240px)` },
                ml: { sm: `240px` },
            }}
        >
            <Container maxWidth="lg">
                <Typography variant="h4" component="h1" gutterBottom>
                    Profile Settings
                </Typography>

                {/* Profile Header */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={3}>
                        <Box position="relative">
                            
                            <Avatar
                                src={user?.profile_picture || user?.profilePictureUrl || profileData?.profilePictureUrl}
                                sx={{ width: 100, height: 100 }}
                            >
                                {user?.first_name?.[0]}{user?.last_name?.[0]}
                            </Avatar>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="profile-picture-upload"
                                type="file"
                                onChange={handleProfilePictureUpload}
                            />
                            <label htmlFor="profile-picture-upload">
                                <IconButton
                                    component="span"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': { backgroundColor: 'primary.dark' },
                                    }}
                                    size="small"
                                >
                                    <PhotoCameraIcon fontSize="small" />
                                </IconButton>
                            </label>
                        </Box>
                        <Box flex={1}>
                            <Typography variant="h5">
                                {user?.first_name} {user?.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {user?.email}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                {user?.roles?.map((role) => (
                                    <Chip key={role} label={role} size="small" sx={{ mr: 1 }} />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                {/* Profile Tabs */}
                <Paper sx={{ mb: 3 }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        aria-label="profile tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab icon={<PersonIcon />} label="Personal Info" />
                        <Tab icon={<SecurityIcon />} label="Security" />
                        {/* <Tab icon={<HistoryIcon />} label="Activity" />
                        <Tab icon={<ApiIcon />} label="API Tokens" />
                        <Tab icon={<SettingsIcon />} label="Preferences" /> */}
                    </Tabs>

                    {/* Personal Information Tab */}
                    <TabPanel value={currentTab} index={0}>
                        <Stack spacing={3}>
                            <Box display="flex" gap={2}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    value={profileForm.firstName}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                                />
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    value={profileForm.lastName}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                                />
                            </Box>
                            <Box display="flex" gap={2}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={profileForm.email}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                                />
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    value={profileForm.phoneNumber}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                />
                            </Box>
                            <Box>
                                <Button
                                    variant="contained"
                                    onClick={handleProfileUpdate}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
                                >
                                    Update Profile
                                </Button>
                            </Box>
                        </Stack>
                    </TabPanel>

                    {/* Security Tab */}
                    <TabPanel value={currentTab} index={1}>
                        <Typography variant="h6" gutterBottom>
                            Change Password
                        </Typography>
                        <Stack spacing={3}>
                            <TextField
                                fullWidth
                                label="Current Password"
                                type="password"
                                value={passwordForm.currentPassword}
                                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                            />
                            <Box display="flex" gap={2}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    type="password"
                                    value={passwordForm.newPassword}
                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                                />
                                <TextField
                                    fullWidth
                                    label="Confirm New Password"
                                    type="password"
                                    value={passwordForm.confirmPassword}
                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                />
                            </Box>
                            <Box>
                                <Button
                                    variant="contained"
                                    onClick={handlePasswordChange}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <SecurityIcon />}
                                >
                                    Change Password
                                </Button>
                            </Box>
                        </Stack>
                    </TabPanel>

                    {/* Activity Tab */}
                    <TabPanel value={currentTab} index={2}>
                        <Typography variant="h6" gutterBottom>
                            Recent Activity
                        </Typography>
                        <List>
                            {activities.map((activity) => (
                                <ListItem key={activity.id} divider>
                                    <ListItemText
                                        primary={activity.action}
                                        secondary={`${activity.description} • ${new Date(activity.timestamp).toLocaleString()}`}
                                    />
                                </ListItem>
                            ))}
                            {activities.length === 0 && (
                                <ListItem>
                                    <ListItemText primary="No recent activity" />
                                </ListItem>
                            )}
                        </List>
                    </TabPanel>

                    {/* API Tokens Tab */}
                    <TabPanel value={currentTab} index={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                API Tokens
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setTokenDialog(true)}
                            >
                                Create Token
                            </Button>
                        </Box>
                        <List>
                            {apiTokens.map((token) => (
                                <ListItem key={token.id} divider>
                                    <ListItemText
                                        primary={token.name}
                                        secondary={`Created: ${new Date(token.createdAt).toLocaleDateString()}`}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            onClick={() => handleRevokeApiToken(token.id)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                            {apiTokens.length === 0 && (
                                <ListItem>
                                    <ListItemText primary="No API tokens created" />
                                </ListItem>
                            )}
                        </List>
                    </TabPanel>

                    {/* Preferences Tab */}
                    <TabPanel value={currentTab} index={4}>
                        <Typography variant="h6" gutterBottom>
                            Notification Preferences
                        </Typography>
                        {settings && (
                            <Stack spacing={3}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.emailNotifications}
                                            onChange={(e) => handleSettingsUpdate({ emailNotifications: e.target.checked })}
                                        />
                                    }
                                    label="Email Notifications"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.smsNotifications}
                                            onChange={(e) => handleSettingsUpdate({ smsNotifications: e.target.checked })}
                                        />
                                    }
                                    label="SMS Notifications"
                                />
                                <Box display="flex" gap={2}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Theme"
                                        value={settings.theme}
                                        onChange={(e) => handleSettingsUpdate({ theme: e.target.value as any })}
                                    >
                                        <MenuItem value="light">Light</MenuItem>
                                        <MenuItem value="dark">Dark</MenuItem>
                                        <MenuItem value="auto">Auto</MenuItem>
                                    </TextField>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Language"
                                        value={settings.language}
                                        onChange={(e) => handleSettingsUpdate({ language: e.target.value })}
                                    >
                                        <MenuItem value="en">English</MenuItem>
                                        <MenuItem value="es">Spanish</MenuItem>
                                        <MenuItem value="fr">French</MenuItem>
                                    </TextField>
                                </Box>
                            </Stack>
                        )}
                    </TabPanel>
                </Paper>

                {/* Create API Token Dialog */}
                <Dialog open={tokenDialog} onClose={() => setTokenDialog(false)}>
                    <DialogTitle>Create API Token</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Token Name"
                            fullWidth
                            variant="outlined"
                            value={tokenName}
                            onChange={(e) => setTokenName(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setTokenDialog(false)}>Cancel</Button>
                        <Button onClick={handleCreateApiToken} variant="contained">
                            Create
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    <Alert
                        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
        </PageLayout>
    );
};

export default ProfilePage;
